import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const SecuritySettings = ({ user, onUpdate }) => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.twoFactorEnabled || false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeApiKeys] = useState([
    {
      id: 'key-001',
      name: 'Production API Key',
      prefix: 'vibe_prod_',
      lastUsed: '2025-01-05T09:30:00Z',
      permissions: ['read', 'write'],
      createdAt: '2024-12-01T10:00:00Z'
    },
    {
      id: 'key-002', 
      name: 'Development Key',
      prefix: 'vibe_dev_',
      lastUsed: '2025-01-04T15:20:00Z',
      permissions: ['read'],
      createdAt: '2024-12-15T14:30:00Z'
    },
    {
      id: 'key-003',
      name: 'Testing Environment',
      prefix: 'vibe_test_',
      lastUsed: '2024-12-20T11:45:00Z',
      permissions: ['read', 'write'],
      createdAt: '2024-11-20T09:15:00Z'
    }
  ]);

  const [activeSessions] = useState([
    {
      id: 'session-001',
      device: 'Chrome auf Windows',
      location: 'Berlin, Deutschland',
      ipAddress: '192.168.1.100',
      lastActive: '2025-01-05T10:00:00Z',
      isCurrent: true
    },
    {
      id: 'session-002',
      device: 'Safari auf iPhone',
      location: 'Berlin, Deutschland', 
      ipAddress: '192.168.1.101',
      lastActive: '2025-01-04T18:30:00Z',
      isCurrent: false
    },
    {
      id: 'session-003',
      device: 'Firefox auf MacOS',
      location: 'München, Deutschland',
      ipAddress: '10.0.0.50',
      lastActive: '2025-01-03T14:20:00Z',
      isCurrent: false
    }
  ]);

  const handle2FAToggle = async () => {
    setIsLoading(true);

    if (!twoFactorEnabled) {
      // Enable 2FA - show QR code
      setShowQRCode(true);
    } else {
      // Disable 2FA
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setTwoFactorEnabled(false);
        onUpdate?.({ twoFactorEnabled: false });
      } catch (error) {
        console.error('Failed to disable 2FA:', error);
      }
    }
    
    setIsLoading(false);
  };

  const verify2FA = async () => {
    if (!verificationCode || verificationCode?.length !== 6) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setTwoFactorEnabled(true);
      setShowQRCode(false);
      setVerificationCode('');
      onUpdate?.({ twoFactorEnabled: true });
    } catch (error) {
      console.error('2FA verification failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateApiKey = () => {
    // In real app, this would call an API
    const newKey = `vibe_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    navigator.clipboard?.writeText(newKey);
    alert(`Neuer API-Schlüssel wurde in die Zwischenablage kopiert:\n${newKey}`);
  };

  const revokeApiKey = (keyId) => {
    if (confirm('Sind Sie sicher, dass Sie diesen API-Schlüssel widerrufen möchten?')) {
      // In real app, this would call an API
      alert(`API-Schlüssel ${keyId} wurde widerrufen`);
    }
  };

  const terminateSession = (sessionId) => {
    if (confirm('Sind Sie sicher, dass Sie diese Sitzung beenden möchten?')) {
      // In real app, this would call an API
      alert(`Sitzung ${sessionId} wurde beendet`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Vor wenigen Minuten';
    } else if (diffInHours < 24) {
      return `Vor ${diffInHours} Stunde${diffInHours > 1 ? 'n' : ''}`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Vor ${diffInDays} Tag${diffInDays > 1 ? 'en' : ''}`;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Sicherheitseinstellungen</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Zwei-Faktor-Authentifizierung, API-Schlüssel und aktive Sitzungen verwalten
        </p>
      </div>
      {/* Two-Factor Authentication */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center",
              twoFactorEnabled ? "bg-success/10" : "bg-muted"
            )}>
              <Icon 
                name={twoFactorEnabled ? "Shield" : "ShieldOff"} 
                size={16} 
                className={twoFactorEnabled ? "text-success" : "text-muted-foreground"}
              />
            </div>
            <div>
              <h3 className="text-lg font-medium text-foreground">Zwei-Faktor-Authentifizierung (2FA)</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Zusätzliche Sicherheitsebene für Ihr Konto mit einer Authenticator-App
              </p>
            </div>
          </div>

          <Button
            onClick={handle2FAToggle}
            variant={twoFactorEnabled ? "destructive" : "default"}
            size="sm"
            loading={isLoading}
            iconName={twoFactorEnabled ? "ShieldOff" : "Shield"}
          >
            {twoFactorEnabled ? "2FA deaktivieren" : "2FA aktivieren"}
          </Button>
        </div>

        {twoFactorEnabled && (
          <div className="mt-4 p-3 bg-success/10 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">
                Zwei-Faktor-Authentifizierung ist aktiviert
              </span>
            </div>
            <p className="text-sm text-success/80 mt-1">
              Ihr Konto ist durch eine zusätzliche Sicherheitsebene geschützt.
            </p>
          </div>
        )}

        {showQRCode && (
          <div className="mt-6 p-4 border border-border rounded-lg">
            <h4 className="text-md font-medium text-foreground mb-3">QR-Code scannen</h4>
            
            {/* Mock QR Code */}
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="w-40 h-40 bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="QrCode" size={48} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">QR-Code</p>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <p className="text-sm text-foreground mb-2">
                    1. Öffnen Sie Ihre Authenticator-App (Google Authenticator, Authy, etc.)
                  </p>
                  <p className="text-sm text-foreground mb-2">
                    2. Scannen Sie den QR-Code oder geben Sie diesen Code manuell ein:
                  </p>
                  <code className="block text-xs bg-muted p-2 rounded font-mono">
                    JBSWY3DPEHPK3PXP
                  </code>
                </div>
                
                <div className="space-y-3">
                  <Input
                    label="Bestätigungscode eingeben"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                    placeholder="6-stelliger Code"
                    maxLength={6}
                  />
                  
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => {
                        setShowQRCode(false);
                        setVerificationCode('');
                      }}
                      variant="outline"
                      size="sm"
                      disabled={isLoading}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={verify2FA}
                      loading={isLoading}
                      disabled={verificationCode?.length !== 6}
                      size="sm"
                    >
                      2FA aktivieren
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* API Keys */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-foreground">API-Schlüssel</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Verwalten Sie API-Schlüssel für den programmatischen Zugriff
            </p>
          </div>
          <Button
            onClick={generateApiKey}
            iconName="Plus"
            iconPosition="left"
            size="sm"
          >
            Neuer API-Schlüssel
          </Button>
        </div>

        <div className="space-y-4">
          {activeApiKeys?.map((apiKey) => (
            <div key={apiKey?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-sm font-medium text-foreground">{apiKey?.name}</h4>
                    <div className="flex gap-1">
                      {apiKey?.permissions?.map((permission) => (
                        <span
                          key={permission}
                          className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                        >
                          {permission}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Key" size={12} />
                      <code className="font-mono">{apiKey?.prefix}...</code>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>Zuletzt verwendet: {formatDate(apiKey?.lastUsed)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      <span>Erstellt: {new Date(apiKey?.createdAt)?.toLocaleDateString('de-DE')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="Copy"
                    onClick={() => {
                      navigator?.clipboard?.writeText(`${apiKey?.prefix}example_key_${apiKey?.id}`);
                      alert('API-Schlüssel in Zwischenablage kopiert');
                    }}
                  />
                  <Button
                    variant="destructive"
                    size="xs"
                    iconName="Trash2"
                    onClick={() => revokeApiKey(apiKey?.id)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {activeApiKeys?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Key" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-sm font-medium text-foreground mb-2">Keine API-Schlüssel</h4>
            <p className="text-sm text-muted-foreground">
              Erstellen Sie Ihren ersten API-Schlüssel für den programmatischen Zugriff.
            </p>
          </div>
        )}
      </div>
      {/* Active Sessions */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-foreground">Aktive Sitzungen</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Übersicht aller Geräte, auf denen Sie angemeldet sind
            </p>
          </div>
          <Button
            variant="outline"
            iconName="LogOut"
            iconPosition="left"
            size="sm"
            onClick={() => {
              if (confirm('Möchten Sie sich von allen anderen Geräten abmelden?')) {
                alert('Von allen anderen Sitzungen abgemeldet');
              }
            }}
          >
            Alle anderen abmelden
          </Button>
        </div>

        <div className="space-y-4">
          {activeSessions?.map((session) => (
            <div key={session?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Monitor" size={16} className="text-muted-foreground" />
                    <h4 className="text-sm font-medium text-foreground">{session?.device}</h4>
                    {session?.isCurrent && (
                      <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                        Aktuelle Sitzung
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="MapPin" size={12} />
                      <span>{session?.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Wifi" size={12} />
                      <span>{session?.ipAddress}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>Zuletzt aktiv: {formatDate(session?.lastActive)}</span>
                    </div>
                  </div>
                </div>
                
                {!session?.isCurrent && (
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="LogOut"
                    onClick={() => terminateSession(session?.id)}
                  >
                    Beenden
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Security Recommendations */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Sicherheitsempfehlungen</h3>
        
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-success/5 border border-success/20">
            <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Zwei-Faktor-Authentifizierung aktiviert</p>
              <p className="text-xs text-muted-foreground">Ihr Konto ist gut geschützt</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/5 border border-warning/20">
            <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Passwort-Sicherheit überprüfen</p>
              <p className="text-xs text-muted-foreground">
                Stellen Sie sicher, dass Ihr Passwort stark und einzigartig ist
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">API-Schlüssel regelmäßig rotieren</p>
              <p className="text-xs text-muted-foreground">
                Erneuern Sie Ihre API-Schlüssel alle 90 Tage für optimale Sicherheit
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;